"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Mascot } from "@/components/landing/Mascot";
import { Reveal } from "@/components/landing/Reveal";

export function QuickStart() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">(
    "idle"
  );
  const [error, setError] = useState("");
  const [demoLoading, setDemoLoading] = useState(false);

  // Capture ?ref= attribution param if present.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const r = new URLSearchParams(window.location.search).get("ref");
    if (r) setRef(r);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setError("");
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "quickstart", ref }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? "Something went wrong. Please try again.");
        setStatus("error");
        return;
      }
      setStatus("done");
    } catch {
      setError("Network error. Please try again.");
      setStatus("error");
    }
  }

  async function handleDemo() {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      const res = await fetch("/api/auth/demo", {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        router.push("/game");
        return;
      }
    } catch {
      // fall through to register on failure
    }
    setDemoLoading(false);
    router.push("/register");
  }

  return (
    <section id="quickstart" className="relative py-16 px-4 bg-cream section-sun">
      <div className="max-w-2xl mx-auto text-center">
        <Reveal>
          <div className="flex items-center justify-center mb-4">
            <Mascot spriteKey="surggie_4" scale={4} className="wiggle" />
          </div>
          <p className="text-[var(--ink-soft)] font-mono text-xs uppercase tracking-widest mb-4">
            Jump right in
          </p>
          <h2
            className="text-3xl md:text-4xl font-black text-[var(--ink)] mb-8"
            style={{ fontFamily: "var(--font-manrope)" }}
          >
            Build Your <span className="gold-shimmer">Empire</span>
          </h2>
        </Reveal>

        <Reveal delayMs={120}>
          {status === "done" ? (
            <div className="card-cute p-6 max-w-md mx-auto">
              <div className="text-4xl mb-2">🎉</div>
              <p
                className="font-black text-xl text-[var(--ink)] mb-2"
                style={{ fontFamily: "var(--font-manrope)" }}
              >
                You&apos;re on the list!
              </p>
              <p className="text-[var(--ink-soft)] font-mono text-xs mb-4">
                We&apos;ll be in touch. Want to start now?
              </p>
              <Link
                href="/register"
                className="btn-gold sticker-press inline-block px-6 py-3 font-mono text-sm uppercase tracking-wider"
                style={{ fontFamily: "var(--font-manrope)" }}
              >
                Create Account →
              </Link>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3 max-w-md mx-auto"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 bg-white border-[3px] border-[var(--ink)] text-[var(--ink)] placeholder-[var(--ink-soft)] font-mono text-sm px-4 py-3 rounded-2xl sticker-shadow-sm focus:outline-none focus:border-[var(--gold-deep)] transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-gold sticker-press px-6 py-3 font-mono text-sm uppercase tracking-wider whitespace-nowrap disabled:opacity-60"
                  style={{ fontFamily: "var(--font-manrope)" }}
                >
                  {status === "loading" ? "Joining…" : "Get Early Access →"}
                </button>
              </div>
              {status === "error" && (
                <p className="text-[#c54a4a] font-mono text-xs">{error}</p>
              )}
            </form>
          )}
        </Reveal>

        <Reveal delayMs={200}>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleDemo}
              disabled={demoLoading}
              className="font-mono text-xs uppercase tracking-wider text-[var(--ink-soft)] underline underline-offset-4 hover:text-[var(--ink)] transition-colors disabled:opacity-60"
            >
              {demoLoading ? "Loading demo…" : "Play demo (no signup) →"}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
